// Finishing a job — or reopening one finished by mistake.
//
// A DIALOG OF ITS OWN, not a field in the edit form. The status column has existed since
// the first jobs migration and was displayed on every screen, but nothing could ever set
// it: the edit form echoed the stored value back so the save would not write a NULL, and
// that was the whole of its life. So every job read "פתוח" forever. This is the missing
// act, and it is deliberately an act — "this work is done" is a decision, while edit is
// for correcting what the job says.
//
// NO MONEY MOVES, and the act is reversible, so the dialog asks its one question and
// explains nothing. No calculation in the system reads a job's
// status at all — the settlement selects on income source and issuer, profit is built
// from invoices and expenses, and a topic's costs go on rolling up to its retainer.
//
// COMPLETED AND CANCELLED DIFFER ONLY IN WHAT THEY SAY, exactly as on a retainer. There
// is no "cancelled means write off what was spent" — the expenses stay expenses.
//
// REOPENING RETURNS A JOB TO 'open', not to 'in_progress'. Which of the two it was
// before is not recorded anywhere, and inventing an answer would be worse than the one
// honest state: back on the active list, with its stage a question for whoever picks
// the work up.
import { useState } from 'react';
import { jobsApi } from '../api';
import he from '../i18n/he';
import Modal from './Modal';
import Button from './Button';
import { ErrorText } from './Loader';
import { isJobDone } from './StatusBadge';

// The two end states as a pair of choices rather than a dropdown: there are exactly two,
// each needs a sentence of explanation, and a <select> shows neither at rest.
const Choice = ({ value, current, onPick, label }) => (
  <Button variant={current === value ? '' : 'subtle'} onClick={() => onPick(value)}>
    {label}
  </Button>
);

export default function JobStatusModal({ job, onClose, onSaved }) {
  const done = isJobDone(job.status);
  // Reopening has one destination, so it needs no choice. Finishing starts on neither option: which of the two it was is the whole
  // decision, and preselecting one would answer it for the partner.
  const [choice, setChoice] = useState(done ? 'open' : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      await jobsApi.setStatus(job.id, choice);
      onSaved();
    } catch (e) {
      setError(e);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={done ? he.jobs.reopenJob : he.jobs.closeJob}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!choice || saving}
      saveLabel={done ? he.jobs.reopenJob : he.jobs.closeJobAction}
    >
      <p className="w-modal-lead"><strong>{job.title}</strong></p>

      {!done && (
        <>
          <p>{he.jobs.closeJobChoice}</p>
          <div className="w-choices">
            <Choice value="completed" current={choice} onPick={setChoice} label={he.jobs.closeJobCompleted} />
            <Choice value="cancelled" current={choice} onPick={setChoice} label={he.jobs.closeJobCancelled} />
          </div>
        </>
      )}

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
